import React, { useState, useEffect } from 'react';
import { Briefcase, Heart, TrendingUp, Users, Upload, Loader2, Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import GdprNotice from '@/components/GdprNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const allJobRoles = [
  { key: 'care_assistant', label: 'Care Assistant', description: 'Provide essential daily care and support to residents', icon: Heart },
  { key: 'senior_care_assistant', label: 'Senior Care Assistant', description: 'Lead care teams and mentor junior staff', icon: Users },
  { key: 'registered_nurse', label: 'Registered Nurse', description: 'Deliver clinical care and medication management', icon: Briefcase },
  { key: 'activities_coordinator', label: 'Activities Coordinator', description: 'Plan and deliver engaging activities for residents', icon: Heart },
  { key: 'housekeeping', label: 'Housekeeping', description: 'Keep our home spotless, safe and welcoming for residents', icon: Sparkles },
  { key: 'kitchen_staff', label: 'Kitchen Staff', description: 'Prepare nutritious meals for our residents', icon: Briefcase },
  { key: 'maintenance', label: 'Maintenance', description: 'Keep our facilities safe and well-maintained', icon: TrendingUp },
  { key: 'admin', label: 'Admin', description: 'Support office operations and administration', icon: Briefcase },
  { key: 'manager', label: 'Manager', description: 'Leadership roles in care management', icon: TrendingUp },
];

const benefits = [
  { icon: Users, label: 'Supportive team environment' },
  { icon: TrendingUp, label: 'Ongoing training and development' },
  { icon: Briefcase, label: 'Career progression opportunities' },
  { icon: Heart, label: 'Meaningful and rewarding work' },
];

const CareersSection = () => {
  const [activeRoles, setActiveRoles] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    cover_letter: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchActiveRoles();
  }, []);

  const fetchActiveRoles = async () => {
    try {
      const response = await axios.get(`${API}/job-roles`);
      setActiveRoles(response.data.roles || {});
    } catch (error) {
      console.error('Error fetching job roles:', error);
    }
  };

  const handleApply = (role) => {
    setSelectedRole(role);
    setDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setCvFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('position', selectedRole.label);
      formDataToSend.append('experience', formData.experience);
      formDataToSend.append('cover_letter', formData.cover_letter);
      if (cvFile) {
        formDataToSend.append('cv', cvFile);
      }

      await axios.post(`${API}/job-application`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Application submitted successfully! We will be in touch soon.');
      setFormData({ name: '', email: '', phone: '', experience: '', cover_letter: '' });
      setCvFile(null);
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableRoles = allJobRoles.filter(role => activeRoles[role.key]);

  return (
    <section
      id="careers"
      className="py-20 md:py-32 bg-slate-50"
      data-testid="careers-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a227]/10 rounded-full text-[#c9a227] text-sm font-medium mb-6">
            <Briefcase className="w-4 h-4" />
            Careers
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6"
           
          >
            Join Our Team at Abney Low
          </h2>
          <p className="text-slate-600 text-lg">
            Join a team that is passionate about making a difference. We are always looking for dedicated individuals who share our commitment to providing exceptional care.
          </p>
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 mb-12">
          <h3 className="font-semibold text-[#1e3a5f] text-xl mb-6 text-center">
            Why Work With Us?
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => {
              const IconComponent = benefit.icon;
              return (
                <div key={benefit.label} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-[#c9a227]/10 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-[#c9a227]" />
                  </div>
                  <span className="text-slate-700 font-medium">{benefit.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Job Roles Grid */}
        {availableRoles.length > 0 ? (
          <>
            <h3 className="text-2xl font-bold text-[#1e3a5f] mb-8 text-center">
              Current Opportunities
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {availableRoles.map((role) => {
                const IconComponent = role.icon;
                return (
                  <div
                    key={role.key}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:border-[#c9a227]/30 hover:shadow-lg transition-all duration-300 flex flex-col"
                    data-testid={`job-role-${role.key}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-[#1e3a5f]" />
                    </div>
                    <h4 className="font-bold text-[#1e3a5f] text-lg mb-2">{role.label}</h4>
                    <p className="text-slate-600 text-sm mb-4 flex-grow">{role.description}</p>
                    <Button
                      onClick={() => handleApply(role)}
                      className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full"
                      data-testid={`apply-btn-${role.key}`}
                    >
                      Apply Now
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl">
            <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No positions currently available. Please check back soon.</p>
          </div>
        )}

        {/* Application Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white border border-slate-200 shadow-xl">
            <DialogHeader>
              <DialogTitle className="text-2xl text-[#1e3a5f]">
                Apply for {selectedRole?.label}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="job-application-form">
              <div>
                <Label htmlFor="career-name">Full Name *</Label>
                <Input
                  id="career-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-2 rounded-xl"
                  placeholder="Your full name"
                  data-testid="career-name-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="career-email">Email *</Label>
                  <Input
                    id="career-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-2 rounded-xl"
                    placeholder="your@email.com"
                    data-testid="career-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="career-phone">Phone *</Label>
                  <Input
                    id="career-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="mt-2 rounded-xl"
                    placeholder="Your phone number"
                    data-testid="career-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="career-experience">Relevant Experience *</Label>
                <Input
                  id="career-experience"
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  required
                  className="mt-2 rounded-xl"
                  placeholder="e.g., 3 years in care"
                  data-testid="career-experience-input"
                />
              </div>
              <div>
                <Label htmlFor="career-cover">Cover Letter</Label>
                <Textarea
                  id="career-cover"
                  value={formData.cover_letter}
                  onChange={(e) => setFormData({ ...formData, cover_letter: e.target.value })}
                  className="mt-2 rounded-xl min-h-[100px]"
                  placeholder="Tell us why you'd like to join our team..."
                  data-testid="career-cover-textarea"
                />
              </div>
              <div>
                <Label>Upload CV (PDF, DOC, DOCX - Max 5MB)</Label>
                <div className="mt-2">
                  <label
                    htmlFor="cv-upload"
                    className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#c9a227] hover:bg-slate-50 transition-colors"
                  >
                    <Upload className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-600 text-sm">
                      {cvFile ? cvFile.name : 'Click to upload your CV'}
                    </span>
                  </label>
                  <input
                    id="cv-upload"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="career-cv-input"
                  />
                </div>
              </div>
              <GdprNotice testId="career-gdpr-notice" />
              <Button
                type="submit"
                disabled={isSubmitting || !formData.name || !formData.email || !formData.phone || !formData.experience}
                className="w-full bg-[#1e3a5f] text-white hover:bg-[#162c4a] rounded-full py-3"
                data-testid="career-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default CareersSection;
